import type { GroupColor, GroupIcon } from '@ralph/contracts'
import { GROUP_COLORS, GROUP_ICONS } from '@ralph/contracts'
import clsx from 'clsx'
import { Check } from 'lucide-react'
import { type KeyboardEvent, useRef } from 'react'
import { GROUP_ICON_COMPONENTS, GROUP_ICON_ROWS } from '@/components/groups/group-icons.ts'
import { ACCENT_INK, accentTileStyle } from '@/components/marketplace/accents.ts'
import { useI18n } from '@/i18n/index.ts'

/**
 * A group's icon and colour, chosen from the curated sets (PRD-26 D-6).
 *
 * Each is ONE radiogroup with roving focus: Tab enters on the chosen item and leaves the group,
 * the arrow keys move — left and right by one, up and down by a row of eight for the icons — and
 * Home and End go to the ends. Moving is choosing, which is how a native radio group behaves and
 * what a keyboard user expects of one. Every option has a name ("Calculator", "Moss"), because a
 * swatch or a glyph alone is not a label.
 */

const ROW = 8

const moveFocus = (
  event: KeyboardEvent<HTMLElement>,
  index: number,
  count: number,
  row: number,
  select: (next: number) => void
) => {
  let next: number | null = null
  if (event.key === 'ArrowRight') next = (index + 1) % count
  else if (event.key === 'ArrowLeft') next = (index - 1 + count) % count
  else if (event.key === 'ArrowDown') next = index + row < count ? index + row : index % row
  else if (event.key === 'ArrowUp')
    next = index - row >= 0 ? index - row : Math.min(count - 1, index + row * Math.floor((count - 1 - index) / row))
  else if (event.key === 'Home') next = 0
  else if (event.key === 'End') next = count - 1
  if (next === null) return
  event.preventDefault()
  select(next)
}

export const IconChooser = ({
  value,
  color,
  onChange,
  labelledBy
}: {
  value: GroupIcon
  color: GroupColor
  onChange: (icon: GroupIcon) => void
  labelledBy: string
}) => {
  const t = useI18n().t.groups
  const refs = useRef<Array<HTMLButtonElement | null>>([])
  const select = (index: number) => {
    const icon = GROUP_ICONS[index]
    if (!icon) return
    onChange(icon)
    refs.current[index]?.focus()
  }

  return (
    <div role="radiogroup" aria-labelledby={labelledBy} className="space-y-1.5">
      {GROUP_ICON_ROWS.map((row, rowIndex) => (
        <div key={row.id} className="grid grid-cols-8 gap-1.5" role="presentation">
          {row.icons.map((icon, column) => {
            const index = rowIndex * ROW + column
            const Icon = GROUP_ICON_COMPONENTS[icon]
            const picked = value === icon
            return (
              // biome-ignore lint/a11y/useSemanticElements: a native radio moves in one dimension; this grid moves by row as well, which only the ARIA radio pattern can express.
              <button
                key={icon}
                ref={(node) => {
                  refs.current[index] = node
                }}
                type="button"
                role="radio"
                aria-checked={picked}
                aria-label={t.icons[icon]}
                title={t.icons[icon]}
                tabIndex={picked ? 0 : -1}
                onClick={() => onChange(icon)}
                onKeyDown={(event) => moveFocus(event, index, GROUP_ICONS.length, ROW, select)}
                className={clsx(
                  'flex aspect-square items-center justify-center rounded-full border transition-[transform,box-shadow,border-color] duration-150',
                  'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary',
                  picked
                    ? 'scale-[1.04] border-transparent'
                    : 'border-rule bg-card text-fg-muted hover:border-rule-strong hover:text-fg'
                )}
                style={picked ? accentTileStyle(color) : undefined}
              >
                <Icon size={15} strokeWidth={2} aria-hidden />
              </button>
            )
          })}
        </div>
      ))}
    </div>
  )
}

export const ColorChooser = ({
  value,
  onChange,
  labelledBy
}: {
  value: GroupColor
  onChange: (color: GroupColor) => void
  labelledBy: string
}) => {
  const t = useI18n().t.groups
  const refs = useRef<Array<HTMLButtonElement | null>>([])
  const select = (index: number) => {
    const color = GROUP_COLORS[index]
    if (!color) return
    onChange(color)
    refs.current[index]?.focus()
  }

  return (
    <div role="radiogroup" aria-labelledby={labelledBy} className="flex flex-wrap gap-2">
      {GROUP_COLORS.map((color, index) => {
        const picked = value === color
        return (
          // biome-ignore lint/a11y/useSemanticElements: the same radio pattern as the icons beside it, so the two choosers behave alike under the keyboard.
          <button
            key={color}
            ref={(node) => {
              refs.current[index] = node
            }}
            type="button"
            role="radio"
            aria-checked={picked}
            aria-label={t.colors[color]}
            title={t.colors[color]}
            tabIndex={picked ? 0 : -1}
            onClick={() => onChange(color)}
            onKeyDown={(event) => moveFocus(event, index, GROUP_COLORS.length, GROUP_COLORS.length, select)}
            className={clsx(
              'flex h-[30px] w-[30px] items-center justify-center rounded-full text-white transition-transform duration-150',
              'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary',
              picked ? 'scale-110 ring-2 ring-offset-2 ring-offset-[var(--card-solid)]' : 'hover:scale-105'
            )}
            style={{
              background: ACCENT_INK[color],
              ...(picked ? { ['--tw-ring-color' as string]: ACCENT_INK[color] } : {})
            }}
          >
            {picked && <Check size={13} strokeWidth={3} aria-hidden />}
          </button>
        )
      })}
    </div>
  )
}
